import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';

import { Text } from '../../../cdl/Text/Text';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { InfoPopover } from '../../../common/InfoPopover/InfoPopover';
import { Box } from '../../../common/ui/Box';
import { useDemandDefinitionCalculationContext } from '../../hooks/useDemandDefinitionCalculationContext';
import { isSameProductKey } from '../../isSameProductKey';
import { DemandDefinitionReferencePeriodFormat } from '../../types/DemandDefinitionReferencePeriodFormat';
import type { DemandDefinitionState } from '../../types/DemandDefinitionState';
import type { DemandProductCategoryOption } from '../../types/DemandProductCategoryOption';
import type { DemandProductKey } from '../../types/DemandProductKey';
import { DemandCardSection } from '../DemandCardSection';
import { DemandDefinitionReferencePeriod } from '../DemandDefinitionReferencePeriod';
import { DemandMetricCard } from '../DemandMetricCard';
import { DemandVolume } from '../DemandVolume';

import { ProductCategoryRow } from './ProductCategoryRow';

interface ProductCategorySummary {
    selectedHistoricalVolume: number;
    referencePeriodFrom?: Date;
    referencePeriodTo?: Date;
}

export const ProductCategoriesStep = () => {
    const [lastSummary, setLastSummary] = useState<ProductCategorySummary>();
    const { calculation, retainedCalculation, categories, isCalculationLoading, showHistoricalData } =
        useDemandDefinitionCalculationContext();
    const { setValues, values } = useFormikContext<DemandDefinitionState>();
    const displayedCalculation = calculation ?? retainedCalculation;
    const productKeyVolumes = displayedCalculation?.productKeyVolumes ?? [];
    const selectedProducts = productKeyVolumes.filter(
        (product) =>
            !values.excludedProductCategories.includes(product.category) &&
            !values.excludedProductKeys.some((excludedKey) => isSameProductKey(excludedKey, product.productKey))
    );
    const selectedCategories = categories.filter(
        (category) =>
            !values.excludedProductCategories.includes(category.id) &&
            (productKeyVolumes.every((product) => product.category !== category.id) ||
                selectedProducts.some((product) => product.category === category.id))
    );
    const selectedHistoricalVolume = calculation?.total.historicalVolume;
    const currentSummary =
        selectedHistoricalVolume === undefined
            ? undefined
            : {
                  selectedHistoricalVolume,
                  referencePeriodFrom: values.referencePeriodFrom,
                  referencePeriodTo: values.referencePeriodTo,
              };
    const displayedSummary = isCalculationLoading ? lastSummary : currentSummary ?? lastSummary;

    useEffect(() => {
        if (selectedHistoricalVolume === undefined || isCalculationLoading) {
            return;
        }

        setLastSummary({
            selectedHistoricalVolume,
            referencePeriodFrom: values.referencePeriodFrom,
            referencePeriodTo: values.referencePeriodTo,
        });
    }, [isCalculationLoading, selectedHistoricalVolume, values.referencePeriodFrom, values.referencePeriodTo]);

    const handleCategoryChange = (category: DemandProductCategoryOption, checked: boolean) => {
        setValues({
            ...values,
            excludedProductCategories: checked
                ? values.excludedProductCategories.filter((excludedCategory) => excludedCategory !== category.id)
                : [...values.excludedProductCategories, category.id],
            excludedProductKeys: checked
                ? values.excludedProductKeys.filter(
                      (excludedKey) =>
                          !productKeyVolumes.some(
                              (product) =>
                                  product.category === category.id && isSameProductKey(product.productKey, excludedKey)
                          )
                  )
                : values.excludedProductKeys,
        });
    };

    const handleProductChange = (
        category: DemandProductCategoryOption,
        productKey: DemandProductKey,
        checked: boolean
    ) => {
        const nextExcludedProductKeys = values.excludedProductKeys.filter(
            (excludedKey) => !isSameProductKey(excludedKey, productKey)
        );

        if (!checked) {
            const categoryProductKeys = productKeyVolumes
                .filter((product) => product.category === category.id)
                .map((product) => product.productKey);
            const hasSelectedProductLeft = categoryProductKeys.some(
                (key) =>
                    !isSameProductKey(key, productKey) &&
                    !nextExcludedProductKeys.some((excludedKey) => isSameProductKey(excludedKey, key))
            );

            if (hasSelectedProductLeft) {
                setValues({ ...values, excludedProductKeys: [...nextExcludedProductKeys, productKey] });
                return;
            }

            setValues({
                ...values,
                excludedProductCategories: [...values.excludedProductCategories, category.id],
                excludedProductKeys: nextExcludedProductKeys.filter(
                    (excludedKey) => !categoryProductKeys.some((key) => isSameProductKey(key, excludedKey))
                ),
            });
            return;
        }

        if (values.excludedProductCategories.includes(category.id)) {
            const otherProductKeys = productKeyVolumes
                .filter(
                    (product) => product.category === category.id && !isSameProductKey(product.productKey, productKey)
                )
                .map((product) => product.productKey)
                .filter((key) => !nextExcludedProductKeys.some((excludedKey) => isSameProductKey(excludedKey, key)));

            setValues({
                ...values,
                excludedProductCategories: values.excludedProductCategories.filter(
                    (excludedCategory) => excludedCategory !== category.id
                ),
                excludedProductKeys: [...nextExcludedProductKeys, ...otherProductKeys],
            });
            return;
        }

        setValues({ ...values, excludedProductKeys: nextExcludedProductKeys });
    };

    return (
        <Box display="flex" flexDirection="column" gap={24}>
            {showHistoricalData ? (
                <DemandCardSection isLoading={isCalculationLoading}>
                    <DemandMetricCard
                        label={translate('demandDefinition.selectedCategories')}
                        value={
                            displayedSummary
                                ? formatNumber({
                                      number: selectedCategories.length,
                                      maximumFractionDigits: 0,
                                  })
                                : undefined
                        }
                    />
                    <DemandMetricCard
                        label={translate('demandDefinition.includedProducts')}
                        value={
                            displayedSummary
                                ? formatNumber({
                                      number: selectedProducts.length,
                                      maximumFractionDigits: 0,
                                  })
                                : undefined
                        }
                    />
                    <DemandMetricCard
                        label={translate('demandDefinition.selectedVolume')}
                        value={
                            displayedSummary === undefined ? undefined : (
                                <Box display="flex" alignItems="center" gap={6}>
                                    <Text variant="subtitle1">
                                        <DemandVolume volume={displayedSummary.selectedHistoricalVolume} />
                                    </Text>
                                    <InfoPopover Icon={IconInfoCircle} side="right" width="257px">
                                        <Box display="flex" flexDirection="column" gap={8}>
                                            <Text variant="extraSmall">
                                                {translate(
                                                    'demandDefinition.historicalVolumeReferencePeriodDescription'
                                                )}
                                            </Text>
                                            <Text variant="extraSmall">
                                                <DemandDefinitionReferencePeriod
                                                    formatVariant={DemandDefinitionReferencePeriodFormat.MONTH}
                                                    referencePeriod={{
                                                        from: displayedSummary.referencePeriodFrom,
                                                        to: displayedSummary.referencePeriodTo,
                                                    }}
                                                />
                                            </Text>
                                        </Box>
                                    </InfoPopover>
                                </Box>
                            )
                        }
                    />
                </DemandCardSection>
            ) : null}
            <Box
                display="grid"
                gridTemplateColumns={showHistoricalData ? 'minmax(0, 1fr) 140px 24px' : 'minmax(0, 1fr) 24px'}
                columnGap={8}
                alignItems="center"
                paddingTop={12}
            >
                <Text variant="fieldLabel" color="foreground.muted" marginBottom={4}>
                    {translate('demandDefinition.categories')}
                </Text>
                {categories.map((category) => (
                    <ProductCategoryRow
                        key={category.id}
                        category={category}
                        excludedProductKeys={values.excludedProductKeys}
                        historicalVolume={displayedCalculation?.categoryVolumes[category.id]}
                        isHistoricalVolumeLoading={isCalculationLoading && !displayedCalculation}
                        isSelected={!values.excludedProductCategories.includes(category.id)}
                        products={productKeyVolumes.filter((product) => product.category === category.id)}
                        showHistoricalData={showHistoricalData}
                        onChange={handleCategoryChange}
                        onProductChange={handleProductChange}
                    />
                ))}
            </Box>
        </Box>
    );
};
